import { useEffect, useState } from 'react';

const trustBadges = [
  { icon: 'ri-shield-star-fill', label: '20+ Years Experience' },
  { icon: 'ri-award-fill', label: 'Licensed & Insured' },
  { icon: 'ri-team-fill', label: 'Employee-Owned' },
  { icon: 'ri-heart-fill', label: 'Faith-Based' },
];

const Hero = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="relative w-full min-h-screen flex items-center overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/7d8f2d48a0e6227a4117dd19cf8286e3.jpg"
          alt="Professional electrical work"
          className="w-full h-full object-cover object-center"
        />
      </div>

      {/* Overlay layers */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/95 via-[#0A1628]/80 to-[#0A1628]/40" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628]/80 via-transparent to-[#0A1628]/30" />

      {/* Circuit line motif overlay */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="circuit" x="0" y="0" width="80" height="80" patternUnits="userSpaceOnUse">
              <path d="M0 40 h20 M60 40 h20 M40 0 v20 M40 60 v20" stroke="#00D9FF" strokeWidth="0.5" fill="none"/>
              <circle cx="40" cy="40" r="3" stroke="#00D9FF" strokeWidth="0.5" fill="none"/>
              <circle cx="20" cy="40" r="1.5" fill="#00D9FF"/>
              <circle cx="60" cy="40" r="1.5" fill="#00D9FF"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#circuit)"/>
        </svg>
      </div>

      {/* Bottom glow line */}
      <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#00D9FF]/40 to-transparent" />

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-10 pt-28 pb-24 lg:pb-32">
        <div className="max-w-3xl">
          {/* Pre-label */}
          <div
            className={`transition-all duration-700 delay-100 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <span className="inline-flex items-center gap-2 text-[#00D9FF] text-[11px] font-semibold uppercase tracking-[3px] mb-6">
              <span className="w-6 h-[1px] bg-[#00D9FF]" />
              Kalamazoo&apos;s Trusted Electrical Experts
              <span className="w-6 h-[1px] bg-[#00D9FF]" />
            </span>
          </div>

          {/* Headline */}
          <div
            className={`transition-all duration-700 delay-200 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <h1 className="text-5xl lg:text-7xl font-black leading-[1.05] mb-6 text-white">
              Electrical Work<br />
              Done with{' '}
              <span className="relative inline-block">
                <span className="text-[#00D9FF] [text-shadow:0_0_40px_rgba(0,217,255,0.5)]">Precision</span>
                <span className="absolute -bottom-1 left-0 right-0 h-[2px] bg-gradient-to-r from-[#00D9FF] to-transparent rounded-full" />
              </span>
              <br />
              <span className="text-white">& Pride</span>
            </h1>
          </div>

          {/* Supporting text */}
          <div
            className={`transition-all duration-700 delay-300 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <p className="text-[#8BA8C8] text-lg lg:text-xl leading-relaxed max-w-xl mb-10">
              20+ years of code-compliant installations, clear communication, and master craftsmanship.
              Faith-based, employee-owned, and committed to safety, respect, and exceptional quality.
            </p>
          </div>

          {/* Buttons */}
          <div
            className={`transition-all duration-700 delay-400 flex flex-col sm:flex-row gap-4 mb-14 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <a
              href="#contact"
              className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#00D9FF] hover:bg-[#00c5e8] text-[#0A1628] font-bold text-[15px] uppercase tracking-wider rounded-lg transition-all duration-300 hover:shadow-[0_0_40px_rgba(0,217,255,0.5)] hover:-translate-y-1 cursor-pointer whitespace-nowrap"
            >
              Request a Free Estimate
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-right-line text-base" />
              </span>
            </a>
            <a
              href="tel:+12693700765"
              className="inline-flex items-center justify-center gap-3 px-8 py-4 border-2 border-white/30 hover:border-white/60 text-white font-semibold text-[15px] rounded-lg transition-all duration-300 hover:bg-white/5 cursor-pointer whitespace-nowrap"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-phone-fill text-[#00D9FF]" />
              </span>
              Call (269) 370-0765
            </a>
          </div>

          {/* Trust badges */}
          <div
            className={`transition-all duration-700 delay-500 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <div className="flex flex-wrap gap-3">
              {trustBadges.map((badge) => (
                <div
                  key={badge.label}
                  className="flex items-center gap-2.5 px-4 py-2.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 hover:border-[#00D9FF]/40 hover:bg-white/15 transition-all duration-300 group cursor-default"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={`${badge.icon} text-[#00D9FF] text-sm`} />
                  </span>
                  <span className="text-white text-[12px] font-semibold tracking-wide whitespace-nowrap">{badge.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce opacity-60">
        <span className="text-[10px] text-white uppercase tracking-[3px]">Scroll</span>
        <span className="w-4 h-4 flex items-center justify-center">
          <i className="ri-arrow-down-line text-white text-base" />
        </span>
      </div>
    </section>
  );
};

export default Hero;
